import { forwardRef, useCallback, useState, useRef, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { ChevronDown } from "lucide-react";
import { usePhoneInput, type Country } from "@core/features/form";
import { CountryCode } from "libphonenumber-js";

export type Props = {
  /** Field name */
  name?: string;
  /** Input id */
  id?: string;
  /** Current E.164 phone number value */
  value?: string;
  /** Called when the E.164 value changes */
  onChange?: (value: string) => void;
  /** Called when input loses focus */
  onBlur?: () => void;
  /** Default country code (ISO 3166-1 alpha-2) */
  defaultCountry?: CountryCode;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** aria-describedby attribute */
  "aria-describedby"?: string;
  /** aria-labelledby attribute */
  "aria-labelledby"?: string;
  /** aria-required attribute */
  "aria-required"?: boolean | "true" | "false";
};

/**
 * International phone number input with integrated country code selector.
 * Stores value in E.164 format (e.g., "+15551234567").
 *
 * @example
 * <PhoneInput
 *   value="+15551234567"
 *   onChange={(value) => console.log(value)} // E.164 format
 *   placeholder="Enter phone number"
 * />
 */
export const PhoneInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      id,
      value = "",
      onChange,
      onBlur,
      defaultCountry,
      placeholder = "Enter phone number",
      error,
      disabled,
      className,
      "aria-describedby": ariaDescribedby,
      "aria-labelledby": ariaLabelledby,
      "aria-required": ariaRequired,
    },
    ref
  ) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const {
      countries,
      selectedCountry,
      displayValue,
      setCountry,
      setNationalNumber,
    } = usePhoneInput({
      value,
      defaultCountry,
      onChange,
    });

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target as Node)
        ) {
          setIsDropdownOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Close dropdown on escape
    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setIsDropdownOpen(false);
        }
      };

      if (isDropdownOpen) {
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
      }
    }, [isDropdownOpen]);

    const handleCountrySelect = useCallback(
      (country: Country) => {
        setCountry(country);
        setIsDropdownOpen(false);
      },
      [setCountry]
    );

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setNationalNumber(e.target.value);
      },
      [setNationalNumber]
    );

    const toggleDropdown = useCallback(() => {
      if (!disabled) {
        setIsDropdownOpen((prev) => !prev);
      }
    }, [disabled]);

    return (
      <div className={twMerge("relative flex", className)}>
        {/* Country selector button */}
        <button
          ref={buttonRef}
          type="button"
          onClick={toggleDropdown}
          disabled={disabled}
          className={twMerge(
            "flex items-center gap-1 px-3 py-2 border rounded-l-sm bg-brand-50",
            "hover:bg-brand-100 transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
            "disabled:cursor-not-allowed disabled:bg-brand-100 disabled:text-brand-300",
            error && "border-error-600 border-2 border-r-0",
            !error && "border-brand-800 border-r-0"
          )}
          aria-label={`Select country. Current: ${selectedCountry.name}`}
          aria-expanded={isDropdownOpen}
          aria-haspopup="listbox"
        >
          <span className="text-sm font-medium text-brand-700">{selectedCountry.code}</span>
          <span className="text-sm text-brand-700">+{selectedCountry.callingCode}</span>
          <ChevronDown
            className={twMerge(
              "w-4 h-4 text-brand-500 transition-transform",
              isDropdownOpen && "rotate-180"
            )}
          />
        </button>

        {/* Phone number input */}
        <input
          ref={ref}
          type="tel"
          name={name}
          id={id}
          value={displayValue}
          onChange={handleInputChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          aria-describedby={ariaDescribedby}
          aria-labelledby={ariaLabelledby}
          aria-required={ariaRequired}
          aria-invalid={error}
          autoComplete="tel-national"
          className={twMerge(
            "flex-1 border rounded-r-sm bg-transparent text-sm shadow-sm transition-colors px-3 py-2 min-w-0",
            "placeholder:text-brand-400",
            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
            "disabled:cursor-not-allowed disabled:bg-brand-100 disabled:text-brand-300 disabled:border-brand-300",
            error && "border-error-600 border-2",
            !error && "border-brand-800"
          )}
        />

        {/* Country dropdown */}
        {isDropdownOpen && (
          <div
            ref={dropdownRef}
            role="listbox"
            aria-label="Select country"
            className={twMerge(
              "absolute top-full left-0 mt-1 z-50",
              "w-72 max-h-64 overflow-y-auto",
              "bg-white border border-brand-200 rounded-sm shadow-lg"
            )}
          >
            {countries.map((country) => (
              <button
                key={country.code}
                type="button"
                role="option"
                aria-selected={country.code === selectedCountry.code}
                onClick={() => handleCountrySelect(country)}
                className={twMerge(
                  "w-full flex items-center gap-3 px-3 py-2 text-left",
                  "hover:bg-brand-50 transition-colors",
                  "focus:outline-none focus:bg-brand-100",
                  country.code === selectedCountry.code && "bg-brand-100"
                )}
              >
                <span className="text-sm font-medium text-brand-700 w-8">{country.code}</span>
                <span className="flex-1 text-sm truncate">{country.name}</span>
                <span className="text-sm text-brand-500">+{country.callingCode}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);

PhoneInput.displayName = "PhoneInput";

export default PhoneInput;
